import _ from 'lodash'
import { myAccountService } from '@/services/my-account-service'

/**
 * Current user's details
 * @type {{id: string, username: string, email: string, firstName: string, lastName: string, fullName: string}}
 */
export let MyUser = {}

/**
 * Current user's roles within the active organization
 * @type {Array<string>}
 */
export let MyActiveRoles = []

/**
 * Current user's permissions within the active organization
 * @type {Array<string>}
 */
export let MyActivePermissions = []

/**
 * Active organization
 * @type {{id: string, name: string, type: string?}}
 */
export let MyActiveOrganization = {}

/**
 * Id of organizations that the current user has access to
 * @type {Array<{id: string, name: string, type: string?}>}
 */
export let MyOtherOrganizations = []

/**
 * Object containing feature flags for current organization
 * @type {{}}
 */
export let MyFeatureFlags = {}

/**
 * Object containing notification preferences for the current user
 * @type {{}}
 */
export let MyNotificationPreferences = {}

/**
 * Value representing the viper.deeplens_user.user_id for the VIPER system user.
 * @type {{id: string, username: string}}
 */
export const ViperUser = { id: 'admin@deeplens.ai', username: 'VIPER' }

/**
 * Initializes current user's account
 * @returns {Promise<boolean>}
 */
export async function initializeMyAccount({ newOrgId } = {}) {
  try {
    const myAccount = await myAccountService.myAccount()

    if (!_.isEmpty(myAccount.tenants)) {
      const orgId = newOrgId || findLastOrganizationId(myAccount)

      setMyUser(myAccount)
      setMyOtherOrganizations(myAccount, orgId)
      setMyActiveOrganization(myAccount, orgId)

      await setMyFeatureFlags(orgId)
    }

    return true
  } catch (e) {
    console.warn('Error loading user account. User may not be associated with an organization')
    return false
  }
}

/**
 * Sets the active organization/tenant
 * @param tenants
 * @param roles
 * @param permissions
 * @param id
 */
export function setMyActiveOrganization({ tenants, roles, permissions }, id) {
  localStorage.setItem('last_organization_id', id)
  MyActiveOrganization = _.find(tenants, { id: id })
  MyActiveRoles = _.get(roles, id)
  MyActivePermissions = _.get(permissions, id)
}

// if we move to host based organization switching, this could be done via a cookie
// that is set by a proxy, load balancer, etc
function findLastOrganizationId({ tenants }) {
  let lastId = localStorage.getItem('last_organization_id')
  const myIds = _.map(tenants, 'id')

  if (lastId === null || !_.includes(myIds, lastId)) {
    lastId = _.head(myIds)
  }

  return lastId
}

function setMyOtherOrganizations({ tenants }, currentOrgId) {
  MyOtherOrganizations = _.chain(tenants)
    .filter(t => t.id !== currentOrgId)
    .sortBy('name')
    .value()
}

async function setMyFeatureFlags(orgId) {
  MyFeatureFlags = await myAccountService.featureFlags(orgId)
}

export function setMyNotificationPreferences(notificationPreferences) {
  MyNotificationPreferences = notificationPreferences
}

export function setMyUser(myAccount) {
  MyUser = {
    id: myAccount.id,
    username: myAccount.username,
    email: myAccount.email,
    firstName: myAccount.firstName,
    lastName: myAccount.lastName,
    fullName: myAccount.fullName,
    identityProvider: myAccount.identityProvider
  }
  setMyNotificationPreferences(myAccount.notificationPreferences)
}
